import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  LoaderCircle,
} from "lucide-react";

import { useIngestionJobs } from "./IngestionJobsContext";
import {
  getIngestionPresentation,
  nextDialogueIndex,
} from "./ingestionPresentation";
import "./IngestionFeedback.css";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!media) return undefined;
    const onChange = () => setReduced(media.matches);
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);

  return reduced;
}

function useRotatingDialogue(dialogues) {
  const reducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const signature = dialogues.join("|");

  useEffect(() => {
    setIndex(0);
  }, [signature]);

  useEffect(() => {
    if (reducedMotion || dialogues.length < 2) return undefined;
    const timer = setInterval(() => {
      setIndex((current) => nextDialogueIndex(dialogues.length, current));
    }, 3500);
    return () => clearInterval(timer);
  }, [dialogues.length, reducedMotion, signature]);

  return dialogues[index] || null;
}

function StatusIcon({ tone, spinning }) {
  if (tone === "success") return <Check aria-hidden="true" className="h-3 w-3" />;
  if (tone === "error") return <AlertTriangle aria-hidden="true" className="h-3 w-3" />;
  if (spinning) return <LoaderCircle aria-hidden="true" className="h-3 w-3 animate-spin" />;
  return <span aria-hidden="true" className="ingestion-badge__pulse" />;
}

export function IngestionStatusBadge({
  job,
  compact = false,
  onRetry,
  retryLabel = "Try again",
  onReview,
  actionClassName = "",
  actionDisabled = false,
}) {
  const presentation = useMemo(
    () => job ? getIngestionPresentation(job, { compact }) : null,
    [compact, job],
  );
  const rotatingDialogue = useRotatingDialogue(presentation?.dialogues || []);

  if (!job || !presentation) return null;
  const visibleLabel = rotatingDialogue || presentation.visibleLabel;
  const checking = (job.pollErrorCount || 0) >= 2 && ["queued", "ingesting"].includes(job.status);
  const progressPercent = job.progress?.percent === null || job.progress?.percent === undefined
    ? null
    : Math.max(0, Math.min(100, Number(job.progress.percent) || 0));

  return (
    <span className="ingestion-badge" data-tone={presentation.tone} data-state={job.status}>
      <StatusIcon
        tone={presentation.tone}
        spinning={checking || job.status === "ingesting"}
      />
      <span className="sr-only" aria-live="polite">
        {checking ? "Checking progress" : presentation.stableLabel}
      </span>
      <span key={visibleLabel} aria-hidden="true" className="ingestion-badge__phrase">
        {checking ? "Checking progress…" : visibleLabel}
        {!checking && presentation.progressLabel ? ` · ${presentation.progressLabel}` : ""}
      </span>
      {progressPercent !== null && (
        <span
          className="ingestion-badge__progress"
          role="progressbar"
          aria-label="Ingestion progress"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={progressPercent}
        >
          <span style={{ transform: `scaleX(${progressPercent / 100})` }} />
        </span>
      )}
      {job.status === "failed" && job.retryable && onRetry && (
        <button
          type="button"
          className={`ingestion-badge__action ${actionClassName}`.trim()}
          disabled={actionDisabled}
          onClick={onRetry}
        >
          {retryLabel}
        </button>
      )}
      {job.status === "failed" && !job.retryable && onReview && (
        <button
          type="button"
          className={`ingestion-badge__action ${actionClassName}`.trim()}
          disabled={actionDisabled}
          onClick={onReview}
        >
          Review
        </button>
      )}
    </span>
  );
}

function fallbackJobLabel(job) {
  const labels = {
    sql_expression: "Expression update",
    query_instruction: "Reference query update",
    general_instruction: "General guidance update",
    table_metadata: "Table knowledge update",
    metadata_sync: "Database map update",
    source_cleanup: "Data source cleanup",
  };
  return job.label || labels[job.resourceType] || "Knowledge update";
}

export function IngestionQueueStrip({ sourceDbId = null, resourceTypes = null, className = "" }) {
  const [expanded, setExpanded] = useState(false);
  const {
    jobs,
    inMotionCount,
    needsAttentionCount,
    retryJob,
  } = useIngestionJobs({ sourceDbId, resourceTypes });

  if (jobs.length === 0) return null;

  return (
    <div className={`ingestion-strip ${className}`.trim()} data-expanded={expanded}>
      <button
        type="button"
        className="ingestion-strip__toggle"
        aria-expanded={expanded}
        aria-label={expanded ? "Hide ingestion details" : "Show ingestion details"}
        onClick={() => setExpanded((value) => !value)}
      >
        <LoaderCircle aria-hidden="true" className={inMotionCount ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
        <span className="ingestion-strip__summary">
          {inMotionCount > 0 && (
            <strong>{inMotionCount} {inMotionCount === 1 ? "update" : "updates"} in motion</strong>
          )}
          {inMotionCount > 0 && <span>Genei is learning in the background</span>}
          {needsAttentionCount > 0 && (
            <span>{needsAttentionCount} needs attention</span>
          )}
        </span>
        <ChevronDown aria-hidden="true" className="ingestion-strip__chevron h-4 w-4" />
      </button>

      {expanded && (
        <div className="ingestion-strip__details">
          {jobs.map((job) => {
            const label = fallbackJobLabel(job);
            return (
              <div className="ingestion-strip__job" key={job.jobId}>
                <span className="ingestion-strip__label">{label}</span>
                <IngestionStatusBadge
                  compact
                  job={job}
                  onRetry={job.retryable ? () => retryJob(job.jobId) : undefined}
                  retryLabel={`Try ${label} again`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
